<template>
  <p-child namespace="p-accordion-group" :class="accordionClassList">
    <div
      :class="expanderClassList"
      tabindex="0"
      role="button"
      :aria-expanded="expanded ? 'true': 'false'"
      :aria-controls="'accordion-content' + _uid"
      :aria-label="computedLabel"
      @click.prevent="onTitleClick"
      @keypress.enter="onTitleClick"
      @keypress.space="onTitleClick"
      @keyup="onKeyUp"
      @blur="onBlur"
    >
      <slot tab-index="-1" name="expander" :label="computedLabel" :title="computedLabel" :expanded="expanded">
        <p-icon v-if="expanded === false">
          expand_more
        </p-icon>
        <p-icon v-if="expanded === true">
          expand_less
        </p-icon>
        <span>{{ computedLabel }}</span>
      </slot>
    </div>
    <div :id="'accordion-content' + _uid" ref="accordionContent" v-mutation-observer:subtree="onContentMutated" :class="contentClassList">
      <transition :duration="300">
        <!-- slot needs div wrapper because transition explicitly wants only one child element -->
        <div>
          <slot v-if="expanded"></slot>
        </div>
      </transition>
    </div>
  </p-child>
</template>

<script lang="ts">
  import inputable from '../../mixins/inputable/inputable';
  import mixins from '../../mixins';

  export default mixins(inputable).extend({
    mixins: [inputable],
    props: {
      value: { default: false, type: [Boolean, String] },
      label: { default: undefined, type: String },
      /** @deprecated Use the label prop instead */
      title: { default: undefined, type: String },
    },
    watch: {
      value: {
        immediate: true,
        handler() {
          this.expanded = this.value && this.value !== 'false';
          this.$nextTick(() => {
            this.setMaxHeight();
          });
        },
      },
    },
    data: () => ({
      navigatedUsingKeyboard: false,
      expanded: false,
    }),
    computed: {
      computedLabel(): string {
        // TODO: Can be removed in a version or two since the title prop is deprecated
        return this.label ?? this.title;
      },
      expanderClassList(): Record<string, boolean> {
        return {
          'expander ': true,
          'no-select ': true,
          'keyboard-focus': this.navigatedUsingKeyboard,
        };
      },
      accordionClassList(): Record<string, boolean> {
        return {
          'p-accordion': true,
        };
      },
      contentClassList(): Record<string, boolean> {
        return {
          content: this.expanded === false,
          'content-expanded': this.expanded === true,
        };
      },
    },
    methods: {
      onKeyUp() {
        this.navigatedUsingKeyboard = true;
      },
      onBlur() {
        this.navigatedUsingKeyboard = false;
      },
      setMaxHeight() {
        const content = this.$refs.accordionContent as HTMLElement;
        // Max-heigth has to be set with JS unfortunately
        // https://stackoverflow.com/questions/3508605/how-can-i-transition-height-0-to-height-auto-using-css
        if (content) {
          if (this.expanded) {
            const contentHeight = Array.prototype.reduce.call(content.childNodes, (p, c) => p + (c.offsetHeight || 0), 0) as number;
            const maxHeight = `${contentHeight}px`;
            content.style.maxHeight = maxHeight;
          } else {
            content.style.maxHeight = `${0}px`;
          }
        }
      },
      onContentMutated() {
        this.$debounce(() => {
          this.setMaxHeight();
        }, 50, this)();
      },
      onTitleClick() {
        this.expanded = !this.expanded;
        this.$emit('input', this.expanded);
        this.$emit('click', this.expanded);
        this.$nextTick(() => {
          this.setMaxHeight();
        });
      },
    },
  });
</script>
